import { MoreVertical } from 'lucide-react';
 
interface PrivacyPolicyProps {
  onOpenMenu: () => void;
}
 
export default function PrivacyPolicyPrivate({ onOpenMenu }: PrivacyPolicyProps) {
  return (
    <div className="h-screen md:h-auto bg-[#F5F5F5] flex flex-col">
      <div className="bg-[#FFFFFF] px-4 pt-6 pb-4 flex items-start justify-between border-b border-[#E0E0E0]">
        <h1 className="text-[28px] font-[700] leading-[1.2] text-[#212121] m-0 flex-1 pr-4">
         Privacy Policy
        </h1>
       
        <button
          onClick={onOpenMenu}
          className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center bg-transparent border-0 cursor-pointer flex-shrink-0"
          aria-label="Open menu"
        >
          <MoreVertical className="w-6 h-6 text-brand-primary" />
        </button>
      </div>
 
     <div className="flex-1 overflow-y-auto bg-[#FFFFFF] px-4 py-6">
        <div className="max-w-[640px] mx-auto">
          <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
            Privacy Policy<br></br>
            ISentryx Data Privacy Protocol<br></br>
            Last Updated: February 25, 2026<br></br>
            ISentryx is committed to protecting the privacy and security of our users’ personal information. This Data Privacy Protocol outlines how we collect, use, store, share, and protect your data while using the ISentryx app, a platform designed to connect medical professionals for personal and professional relationships. By using ISentryx, you agree to the practices described in this protocol.
          </p>
          <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
            1. Scope and Applicability
          </h2>
          <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
            This protocol applies to all users of the ISentryx app, it covers all data collected through the app, website, and related services. Due to the medical professional user base, this protocol incorporates safeguards to comply with general data privacy laws (e.g., GDPR, CCPA) and, where applicable, health-related regulations (e.g., HIPAA for U.S. users handling protected health information).
          </p>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              2. Data We Collect
            </h2>

            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              ISentryx collects the following categories of data to provide and
              improve our services:
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              <strong>2.1 Personal Information</strong>
              <br></br>• Account Information: Name, email address, phone number, date of birth, gender, and professional credentials (e.g., medical license number, specialty, workplace).<br></br>
              • Profile Information: Photos, bio, relationship preferences, and other user-provided details to create a profile.<br></br>•
              Verification Data: Documents or information used to verify professional credentials (e.g., medical license or certification details)
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              <strong>2.2 Usage Data</strong>
              <br></br>• Interaction Data: Messages, likes, matches, and other
              in-app interactions.<br></br>• App Usage: Pages visited, time spent on the app, features used, and device information (e.g., IP address, device type, operating system).<br></br>
              • Location Data: Optional geolocation data to suggest nearby matches, collected only with explicit user consent.
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              <strong>2.3 Health-Related Data</strong>
              <br></br>• ISentryx does not collect protected health information (PHI) as defined by HIPAA unless explicitly provided by users in their profiles or communications (e.g., mentioning health conditions in bios). Users are discouraged from sharing PHI in public or private app communications.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              3. How We Use Your Data
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              ISentryx uses your data to provide, personalize, and improve our
              services. Specific uses include:
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              • Account Creation and Verification: To create and authenticate user accounts and verify medical professional status.<br></br>
              • Matching and Communication: To facilitate connections, suggest
              compatible matches, and enable messaging.<br></br>
              • Service Improvement: To analyze usage patterns and enhance app
              functionality and user experience.<br></br>• Marketing and
              Promotions: To send personalized offers, newsletters, or updates
              (with user consent, where required).<br></br>• Legal Compliance
              and Safety: To comply with legal obligations, prevent fraud,
              enforce community guidelines, and protect user safety.<br></br>•
              Analytics: To generate aggregated, anonymized insights for
              internal research or third-party partners (without identifying
              individuals).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              4. Data Sharing and Disclosure
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              ISentryx shares data only in the following circumstances:
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              <strong>4.1 With Other Users</strong>
              <br></br>• Profile information (e.g., name, photos, bio) is shared
              with other users based on your privacy settings and match
              preferences.<br></br>• Messages are shared only with the intended
              recipient(s).
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              <strong>4.2 With Service Providers</strong>
              <br></br>• We share data with trusted third-party providers for
              specific purposes, such as:<br></br>• Cloud storage and hosting(e.g., AWS, Google Cloud).
              <br></br>• Analytics and crash reporting
              (e.g., Google Analytics, Firebase).<br></br>• Customer support
              tools (e.g., Zendesk).<br></br>• All service providers are bound
              by contracts to protect your data and comply with applicable
              privacy laws.
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              <strong>4.3 Legal and Safety Purposes</strong>
              <br></br>• We may disclose data to comply with legal obligations,
              respond to government requests, or protect the rights, safety, or
              property of ISentryx, our users, or the public.<br></br>• Data
              may be shared to investigate or prevent violations of our Terms of
              Service or Community Guidelines.
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              <strong>4.4 Business Transfers</strong>
              <br></br>• In the event of a merger, acquisition, or sale of
              assets, user data may be transferred to the acquiring entity, with
              notice to users and continued protection under this protocol.
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              <strong>4.5 Anonymized Data</strong>
              <br></br>• We may share aggregated, anonymized data with third
              parties for research, marketing, or analytics purposes. This data
              cannot be used to identify individuals.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              5. Data Storage and Security
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              • Storage: Data is stored on secure servers located in the United States<br></br>
              • Encryption: Data is encrypted in transit (using TLS 1.2 or higher) and at rest (using AES-256
              encryption).<br></br>• Access Controls: Only authorized personnel
              have access to user data, and access is limited to what is
              necessary for their role.<br></br>• Security Measures: We
              implement firewalls, intrusion detection systems, and regular
              security audits to protect against unauthorized access or
              breaches.<br></br>• Retention: We retain personal data only for as long as necessary to provide our services or comply with legal obligations. Inactive accounts are deleted after 1 year unless otherwise requested by the user.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              6. User Rights and Controls
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              ISentryx empowers users to control their data. Depending on your jurisdiction, you
              may have the following rights under laws like GDPR or CCPA:
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              • Access: Request a copy of your personal data.<br></br>•
              Correction: Update or correct inaccurate data.<br></br>• Deletion:
              Request deletion of your account and associated data.<br></br>•
              Portability: Obtain your data in a machine-readable format.
              <br></br>• Restriction: Request that we restrict processing of
              your data in certain cases.<br></br>• Objection: Object to
              processing for marketing or other purposes.<br></br>• Opt-Out: Opt
              out of data sharing with third parties (where applicable).
              <br></br>
              To exercise these rights, contact us at{" "}
              <a href="mailto:privacy@ISentryx.com">
                privacy@ISentryx.com
              </a>{" "}
              or use the in-app privacy settings. We will respond
              within 30 days or as required by law.
            </p>
          </section>
          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              7. User Responsibilities
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              • Accuracy: Provide accurate and up-to-date information during
              account creation and verification.<br></br>• Content: Avoid
              sharing sensitive information, such as PHI, in profiles or
              messages, as ISentryx is not a HIPAA-compliant platform for
              handling PHI.<br></br>• Security: Protect your account credentials
              and report any unauthorized access immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              8. Cookies and Tracking Technologies
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              • Cookies: We use cookies and similar technologies (e.g., pixel
              tags) to enhance user experience, track usage(with consent, where required).<br>
              </br>• Do Not Track: ISentryx respects “Do Not Track” browser settings for
              non-essential tracking.<br></br>• Opt-Out: Users can manage cookie
              preferences through their browser or our app settings.
            </p>
          </section>
          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              9. International Data Transfers
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              •  For users outside the United States, data may be transferred to servers in other
              countries. We ensure compliance with international data transfer regulations (e.g., GDPR’s
              Standard Contractual Clauses) to protect your data.
            </p>
          </section>
          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              10. Children’s Privacy
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              • ISentryx is intended for users aged 18 and older. We do not
              knowingly collect data from individuals under 18. If we become
              aware of such data, we will delete it immediately.
            </p>
          </section>
          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              11. Third-Party Links
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              • The app may contain links to third-party websites or services
              (e.g., social media integrations). ISentryx is not responsible
              for the privacy practices of these third parties. Review their
              privacy policies before sharing data.
            </p>
          </section>
          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              12. Data Breach Notification
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              • In the event of a data breach, we will notify affected users and
              relevant authorities within 72 hours, as required by applicable
              laws (e.g., GDPR). We will provide details about the breach and
              steps to protect your data.
            </p>
          </section>
          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              13. Changes to This Protocol
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              • We may update this protocol to reflect changes in our practices
              or legal requirements. Users will be notified of significant
              changes via email or in-app notifications. The updated protocol
              will be posted on our website and app with the effective date.{" "}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              14. Contact Us
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              For questions, concerns, or to exercise your data rights, contact
              our Data Protection Officer at:<br></br>
              Email:{" "}
              <a href="mailto:privacy@ISentryx.com">privacy@ISentryx.com</a>
              <br></br>             
              For California users, you may file a complaint with the California Attorney General.
            </p>
          </section>
          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              15. Compliance with Health Regulations
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              • While ISentryx verifies medical professional credentials, it is
              not a platform for sharing or storing PHI. Users who share
              health-related data do so at their own risk. We encourage users to
              avoid sharing PHI and to use HIPAA-compliant platforms for any
              health-related communications.
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              This protocol is designed to balance user trust, regulatory
              compliance, and the unique needs of a medical professional dating
              app. By adhering to these practices, ISentryx aims to create a
              safe and private environment for our community.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}